import React, { FC } from 'react'
import { Conversation, Tag } from '../hooks/useChat'
import { toAbsoluteUrl } from '../../../../../_metronic/helpers'
import ChatFilters from './ChatFilters'

type Props = {
    conversations: Conversation[]
    selectedId: number | undefined
    onSelect: (c: Conversation) => void
    onNewChat: () => void
    onRefresh: () => void
    loading: boolean
    assignedOnly?: boolean
    onToggleAssignedOnly?: (value: boolean) => void
    filterWhatsapp: boolean
    onToggleWhatsapp: (active: boolean) => void
    filterSms: boolean
    onToggleSms: (active: boolean) => void

    // Search
    searchQuery: string
    onSetSearchQuery: (val: string) => void

    // Advanced Filters Props
    filterUnread: boolean
    onToggleUnread: (val: boolean) => void
    filterDateFrom: string
    onSetDateFrom: (val: string) => void
    filterDateTo: string
    onSetDateTo: (val: string) => void
    filterTags: number[]
    onSetTags: (tags: number[]) => void
    availableTags: Tag[]
}

const ConversationList: FC<Props> = ({
    conversations,
    selectedId,
    onSelect,
    onNewChat,
    onRefresh,
    loading,
    assignedOnly,
    onToggleAssignedOnly,
    filterWhatsapp,
    onToggleWhatsapp,
    filterSms,
    onToggleSms,
    searchQuery,
    onSetSearchQuery,
    filterUnread,
    onToggleUnread,
    filterDateFrom,
    onSetDateFrom,
    filterDateTo,
    onSetDateTo,
    filterTags,
    onSetTags,
    availableTags
}) => {
    const activeFiltersCount =
        (assignedOnly ? 1 : 0) +
        (filterWhatsapp ? 1 : 0) +
        (filterSms ? 1 : 0) +
        (filterUnread ? 1 : 0) +
        (filterDateFrom ? 1 : 0) +
        (filterDateTo ? 1 : 0) +
        filterTags.length

    const clearFilters = () => {
        onToggleUnread(false)
        onSetDateFrom('')
        onSetDateTo('')
        onSetTags([])
        if (onToggleAssignedOnly) onToggleAssignedOnly(false)
        onToggleWhatsapp(false)
        onToggleSms(false)
    }

    return (
        <div className='card card-flush h-100 conversation-list'>
            <div className='card-header d-flex flex-column flex-sm-row pt-7' id='kt_chat_contacts_header'>
                <div className='card-title'>
                    <h2 className='mb-0 me-3'>Conversations</h2>
                    <button
                        className={`btn btn-icon btn-sm btn-light-primary ${loading ? 'opacity-50' : ''}`}
                        onClick={onRefresh}
                        disabled={loading}
                        title="Refrescar conversaciones"
                    >
                        <i className={`bi bi-arrow-clockwise fs-3 ${loading ? 'spinner-border spinner-border-sm' : ''}`}></i>
                    </button>
                </div>
                <div className='card-toolbar mt-3 mt-sm-0'>
                    <button className='btn btn-sm btn-light-primary' onClick={onNewChat}>
                        <i className='bi bi-plus-lg fs-2'></i> New Chat
                    </button>
                    <ChatFilters
                        filterUnread={filterUnread}
                        onToggleUnread={onToggleUnread}
                        filterDateFrom={filterDateFrom}
                        onSetDateFrom={onSetDateFrom}
                        filterDateTo={filterDateTo}
                        onSetDateTo={onSetDateTo}
                        filterTags={filterTags}
                        onSetTags={onSetTags}
                        availableTags={availableTags}
                        assignedOnly={assignedOnly}
                        onToggleAssignedOnly={onToggleAssignedOnly}
                        filterWhatsapp={filterWhatsapp}
                        onToggleWhatsapp={onToggleWhatsapp}
                        filterSms={filterSms}
                        onToggleSms={onToggleSms}
                    />
                    <div className='search-box'>
                        <i className='bi bi-search position-absolute top-50 translate-middle-y ms-3 text-muted'></i>
                        <input
                            type='text'
                            className='form-control form-control-solid ps-10'
                            placeholder='Search by name...'
                            value={searchQuery}
                            onChange={(e) => onSetSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className='px-7 py-3'>

            </div>

            <div className='card-body pt-5' id='kt_chat_contacts_body'>
                <div
                    className='scroll-y me-n5 pe-5 h-200px h-lg-auto'
                    data-kt-scroll='true'
                    data-kt-scroll-activate='{default: false, lg: true}'
                    data-kt-scroll-max-height='auto'
                    data-kt-scroll-dependencies='#kt_header, #kt_app_header, #kt_toolbar, #kt_app_toolbar, #kt_footer, #kt_app_footer, #kt_chat_contacts_header'
                    data-kt-scroll-wrappers='#kt_content, #kt_app_content, #kt_chat_contacts_body'
                    data-kt-scroll-offset='5px'
                    style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}
                >
                    {loading && (
                        <div className="d-flex justify-content-center my-4">
                            <span className="spinner-border text-primary" role="status"></span>
                        </div>
                    )}

                    {!loading && conversations.length === 0 && (
                        <div className="text-center text-muted my-10">
                            {activeFiltersCount > 0 ? (
                                <div className="d-flex flex-column align-items-center">
                                    <div className="mb-3 fs-5">No conversations match your filters</div>
                                    <button
                                        className="btn btn-sm btn-light-primary"
                                        onClick={clearFilters}
                                    >
                                        Clear Filters
                                    </button>
                                </div>
                            ) : (
                                <div>No active conversations</div>
                            )}
                        </div>
                    )}

                    {conversations.map((c) => (
                        <div
                            key={c.id}
                            className={`d-flex flex-stack py-4 cursor-pointer ${selectedId === c.id ? 'bg-light-primary rounded p-2' : ''
                                }`}
                            onClick={() => onSelect(c)}
                        >
                            <div className='d-flex align-items-center'>
                                <div className='symbol symbol-45px symbol-circle'>
                                    {c.avatar ? (
                                        <img alt='Pic' src={c.avatar} />
                                    ) : (
                                        <span className='symbol-label bg-light-danger text-danger fs-6 fw-bolder'>
                                            {c.full_name?.charAt(0) || '?'}
                                        </span>
                                    )}
                                    {/* Status indicator (optional, online/offline requires more logic) */}
                                    {/* <div className='symbol-badge bg-success start-100 top-100 border-4 h-8px w-8px ms-n2 mt-n2'></div> */}
                                </div>

                                <div className='ms-5'>
                                    <span className='fs-5 fw-bolder text-gray-900 text-hover-primary mb-1'>
                                        {c.full_name}
                                    </span>
                                    <div className='fs-8 text-muted mb-2 contact-phone-number'>
                                        {c.phone}
                                    </div>
                                    <div className='fw-bold text-gray-400'>
                                        {c.last_message ? (
                                            <span className='text-truncate d-block' style={{ maxWidth: '150px' }}>
                                                {c.last_message.direction === 'OUTBOUND' && <i className='bi bi-check2-all me-1 text-muted'></i>}
                                                {c.last_message.content}
                                            </span>
                                        ) : (
                                            <span className='text-italic'>No messages</span>
                                        )}
                                    </div>

                                    {c.tags && c.tags.length > 0 && (
                                        <div className='d-flex flex-wrap gap-1 mt-1'>
                                            {c.tags.map(tag => (
                                                <span
                                                    key={tag.id}
                                                    className='badge badge-sm px-1 py-0'
                                                    style={{
                                                        backgroundColor: tag.color + '20',
                                                        color: tag.color,
                                                        border: `1px solid ${tag.color}`,
                                                        fontSize: '0.65rem'
                                                    }}
                                                >
                                                    {tag.name}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className='d-flex flex-column align-items-end ms-2'>
                                <div className="d-flex gap-1 mb-1">
                                    {c.active_channels ? (
                                        <>
                                            {c.active_channels.includes('WHATSAPP') && (
                                                <span className="badge badge-circle badge-light-success w-20px h-20px d-flex align-items-center justify-content-center" title="WhatsApp">
                                                    <i className="bi bi-whatsapp text-success" style={{ fontSize: '0.75rem' }}></i>
                                                </span>
                                            )}
                                            {c.active_channels.includes('SMS') && (
                                                <span className="badge badge-light-secondary fs-9 px-2 py-1" title="SMS" style={{ height: '20px', display: 'flex', alignItems: 'center' }}>
                                                    SMS
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        /* Fallback if active_channels not yet populated */
                                        c.last_message?.channel === 'WHATSAPP' && (
                                            <i className="bi bi-whatsapp text-success"></i>
                                        )
                                    )}
                                </div>
                                <span className='text-muted fs-7 mb-1'>
                                    {c.last_message?.created_at
                                        ? new Date(c.last_message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                        : ''}
                                </span>
                                {c.unread_count > 0 && (
                                    <span className='badge badge-sm badge-circle badge-light-danger'>
                                        {c.unread_count}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}

                    <div className='separator my-2'></div>
                </div>
            </div>
        </div >
    )
}

export { ConversationList }
